import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useRememberListUrl } from '../lib/listReturn';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getToken } from '../lib/api';
import { useAuth, canDelete, can } from '../lib/auth';

interface ClientDto {
  id: string;
  name: string;
  email: string;
  phone: string;
  contactPersonName: string;
  contactPersonPhone: string;
  cin: string;
  tin: string;
  /** Empty until migration_2026_10_10_vendor_client_registration has run. */
  gstNo?: string;
  createdOn: string | null;
  crmUserId: number | null;
  crmName: string | null;
}

interface ListResponse {
  rows: ClientDto[];
  page: number;
  pageSize: number;
  total: number;
}

const PAGE_SIZE = 50;

export function ClientsPage() {
  useRememberListUrl();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get('page') ?? '1'));
  const q = params.get('q') ?? '';
  const [search, setSearch] = useState(q);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['clients', { page, q }],
    queryFn: () =>
      api<ListResponse>(
        `/api/clients?page=${page}&pageSize=${PAGE_SIZE}` + (q ? `&q=${encodeURIComponent(q)}` : ''),
      ),
  });

  const { user } = useAuth();
  const canEdit = can(user, 'EDIT_CLIENTS');
  // Super Admin / VPPL only. The API enforces the same rule, so this hides a
  // control that would otherwise 403 — it is not the access check itself.
  const canAssignCrm = can(user, 'ASSIGN_CRM');
  const [assignError, setAssignError] = useState<string | null>(null);

  const crms = useQuery({
    enabled: canAssignCrm,
    queryKey: ['flow-crms'],
    queryFn: () => api<{ id: number; name: string }[]>('/api/flow/crms'),
    staleTime: 5 * 60_000,
  });

  /**
   * Reassigning writes in two places — the client's own CRM_UserId and its
   * open POs — which is why it goes through the existing /flow/assign-crm
   * endpoint rather than a plain client update. That endpoint does both in one
   * transaction; updating only the client would leave the new CRM's queue empty.
   */
  const assignCrm = useMutation({
    mutationFn: (v: { clientId: string; crmUserId: number }) =>
      api<{ crmName: string; posUpdated: number }>('/api/flow/assign-crm', {
        method: 'POST',
        body: JSON.stringify(v),
      }),
    onSuccess: () => {
      setAssignError(null);
      qc.invalidateQueries({ queryKey: ['clients'] });
      // The queues filter on the PO's own CRM, which just changed underneath them.
      qc.invalidateQueries({ queryKey: ['flow-queue'] });
      qc.invalidateQueries({ queryKey: ['dashboard-pending-jobcards'] });
    },
    onError: (e) => {
      const m = (e as Error).message;
      setAssignError(
        m === 'crm_not_found' ? 'That CRM account is no longer active — refresh and pick another.'
        : m === 'forbidden' ? 'Your account cannot reassign clients.'
        : m,
      );
    },
  });

  const del = useMutation({
    mutationFn: (ids: string[]) =>
      api('/api/clients/delete', { method: 'POST', body: JSON.stringify({ ids }) }),
    onSuccess: () => {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    const p = new URLSearchParams(params);
    if (search) p.set('q', search);
    else p.delete('q');
    p.set('page', '1');
    setParams(p);
  }

  function goPage(next: number) {
    const p = new URLSearchParams(params);
    p.set('page', String(next));
    setParams(p);
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleAll(rows: ClientDto[]) {
    setSelected((prev) => {
      if (prev.size === rows.length) return new Set();
      return new Set(rows.map((r) => r.id));
    });
  }

  function downloadCsv() {
    const url = '/api/clients/export.csv' + (q ? `?q=${encodeURIComponent(q)}` : '');
    const token = getToken();
    // For a token-authed download, fetch as blob then trigger download.
    fetch(url, { headers: token ? { authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `clients-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      });
  }

  const rows = list.data?.rows ?? [];
  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-brand">Clients</h1>
          <p className="text-xs text-slate-500">{total.toLocaleString()} active client{total === 1 ? '' : 's'}</p>
        </div>
        <div className="flex gap-2">
          <button className="btn-ghost" onClick={downloadCsv}>Export CSV</button>
          <Link to="/clients/blank/print" className="btn-ghost" title="An empty client form to print and fill in by hand">
            Blank Form
          </Link>
          {canEdit && (
          <Link to="/clients/new" className="btn-primary">+ New Client</Link>
        )}
        </div>
      </div>

      <form className="flex flex-wrap items-end gap-2" onSubmit={applySearch}>
        <div className="flex-1 min-w-[240px]">
          <label className="label" htmlFor="search">Search</label>
          <input
            id="search" className="input" placeholder="Name, ID, email, phone…"
            value={search} onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button className="btn-primary" type="submit">Search</button>
        {q && (
          <button
            type="button" className="btn-ghost"
            onClick={() => { setSearch(''); setParams({}); }}
          >
            Reset
          </button>
        )}
        {canDelete(user) && selected.size > 0 && (
          <button
            type="button" className="btn-danger"
            disabled={del.isPending}
            onClick={() => {
              if (confirm(`Soft-delete ${selected.size} client(s)?`)) {
                del.mutate(Array.from(selected));
              }
            }}
          >
            Delete {selected.size}
          </button>
        )}
      </form>

      {assignError && (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {assignError}
        </div>
      )}
      {assignCrm.isSuccess && !assignError && (
        <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          Reassigned to {assignCrm.data.crmName}. {assignCrm.data.posUpdated} open order
          {assignCrm.data.posUpdated === 1 ? '' : 's'} moved into their queue.
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <caption className="sr-only">Clients</caption>
            <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600">
              <tr>
                {canDelete(user) && <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={rows.length > 0 && selected.size === rows.length}
                    onChange={() => toggleAll(rows)}
                  />
                </th>}
                <th className="px-3 py-2">ID</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Contact</th>
                <th className="px-3 py-2">Phone</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">GST No.</th>
                {/* Reassignable in place, so ownership can be corrected later
                    without going through the dashboard's unmapped-only panel. */}
                {canAssignCrm && <th className="px-3 py-2">CRM</th>}
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">Loading…</td></tr>
              )}
              {list.isError && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-red-600">
                  Failed to load: {(list.error as Error).message}
                </td></tr>
              )}
              {!list.isLoading && rows.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">No records found.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                  {canDelete(user) && <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggle(r.id)}
                    />
                  </td>}
                  <td className="px-3 py-2 font-mono text-xs">{r.id}</td>
                  <td className="px-3 py-2 font-medium">{r.name}</td>
                  <td className="px-3 py-2 text-slate-600">{r.contactPersonName}</td>
                  <td className="px-3 py-2 text-slate-600">{r.phone || r.contactPersonPhone}</td>
                  <td className="px-3 py-2 text-slate-600">{r.email}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {r.gstNo || (r.tin ? <span title="TIN Number (old)">{r.tin}</span> : '')}
                  </td>
                  {canAssignCrm && (
                    <td className="px-3 py-2">
                      <select
                        className="input py-1 text-xs w-36"
                        value={r.crmUserId == null ? '' : String(r.crmUserId)}
                        disabled={assignCrm.isPending}
                        onChange={(e) => assignCrm.mutate({ clientId: r.id, crmUserId: Number(e.target.value) })}
                      >
                        {/* No blank option: assign-crm takes a positive user id,
                            so "unassign" is not something this control can do —
                            offering it would produce a 400 the user cannot act on. */}
                        {r.crmUserId == null && <option value="">— none —</option>}
                        {crms.data?.map((c) => (
                          <option key={c.id} value={String(c.id)}>{c.name}</option>
                        ))}
                      </select>
                    </td>
                  )}
                  <td className="px-3 py-2 text-right space-x-2">
                    <Link to={`/clients/${r.id}/print`} className="text-slate-600 hover:underline text-sm">Print</Link>
                    <Link to={`/clients/${r.id}/edit`} className="text-brand hover:underline text-sm">{canEdit ? 'Edit' : 'View'}</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm">
        <div className="text-slate-500">
          Page {page} of {totalPages}
        </div>
        <div className="flex gap-2">
          <button className="btn-ghost" disabled={page <= 1} onClick={() => goPage(page - 1)}>
            ‹ Prev
          </button>
          <button className="btn-ghost" disabled={page >= totalPages} onClick={() => goPage(page + 1)}>
            Next ›
          </button>
        </div>
      </div>
    </div>
  );
}
